/* Scene for systemet i tilbodet: ei rein, moderne ramme (ingen nettlesarkrom) som legg systemet ut for si eiga breidde og
   skalerer det inn. full ≥ fullMin (sidemeny) · compact (smalare, utan sidemeny) · narrow (telefon). Ikkje-interaktive
   visingar blir pakka i Inert: ingen peikarhendingar, ingen tab-stopp, ingen lenker. */
import { useEffect, useRef, useState } from "react";

export const geoFor = (w, { fullWidth = 1360, compactMin = 1040, narrowMin = 390, maxScale = 1, fullMin = 1100, narrowMax = 700 } = {}) => {
  const mode = w < narrowMax ? "narrow" : w < fullMin ? "compact" : "full";
  const vw = mode === "narrow" ? Math.max(narrowMin, w) : mode === "compact" ? Math.max(compactMin, w) : Math.max(fullWidth, 1);
  return { mode, vw, s: Math.min(maxScale, w / vw) };
};

export const reducedMotion = () => typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

/* held ekte navigasjon ute av ei forhandsvising: ingen peikarhendingar, ingen tab-stopp, ingen lenker */
export const Inert = ({ children, className = "", style }) => {
  const ref = useRef(null);
  useEffect(() => {
    ref.current?.setAttribute("inert", "");
  }, []);
  return (
    <div ref={ref} className={`pointer-events-none select-none ${className}`} style={style} aria-hidden="true">
      {children}
    </div>
  );
};

/* breidde (→ geometri) + synlegheit for eit element */
export const useGeo = (ref, widths) => {
  const [geo, setGeo] = useState(() => geoFor(widths?.fullWidth || 1360, widths));
  const [active, setActive] = useState(false);
  const [w, setW] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(([e]) => {
      setW(e.contentRect.width);
      setGeo(geoFor(e.contentRect.width, typeof widths === "function" ? widths(e.contentRect.width) : widths));
    });
    ro.observe(el);
    if (typeof IntersectionObserver === "undefined") {
      setActive(true);
      return () => ro.disconnect();
    }
    const io = new IntersectionObserver((entries) => setActive(entries[entries.length - 1].isIntersecting), { threshold: 0.05 });
    io.observe(el);
    return () => {
      ro.disconnect();
      io.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return [geo, active, w];
};

/* ramma: innhaldet lagt ut i design-breidda og skalert inn; høgda følgjer skalaen */
export const Stage = ({ height = 860, narrowHeight = 720, widths, children, testId, className = "", interactive = false }) => {
  const ref = useRef(null);
  const [geo, active] = useGeo(ref, widths);
  const ih = typeof height === "function" ? height(geo) : geo.mode === "narrow" ? narrowHeight : height;
  const body = (
    <div className="sys-scale" style={{ transform: `scale(${geo.s})`, width: geo.vw, height: ih }}>
      {children({ mode: geo.mode, active, width: geo.vw, height: ih, scale: geo.s })}
    </div>
  );
  return (
    <div ref={ref} className={`sys-frame ${className}`} style={{ height: Math.round(ih * geo.s) }} data-testid={testId} data-mode={geo.mode} data-active={active ? "1" : "0"}>
      {interactive ? body : <Inert className="sys-inert">{body}</Inert>}
    </div>
  );
};
